/**
 * Analytics setup for web application (lazy-loaded to reduce first-load JS)
 */

import type { Analytics } from '@repo/analytics';
import { getAnonymousId } from '@/lib/anon-id';

// ---------------------------------------------------------------------------
// PostHog error-tracking noise filter
//
// PostHog captures uncaught exceptions globally (`capture_exceptions: true`).
// Without filtering, the Error Tracking dashboard fills up with errors that
// (a) we already self-heal, (b) come from browser quirks not our code, or
// (c) originate in third-party scripts we don't control. This helper is the
// single place where we decide what NEVER reaches PostHog.
//
// Principles:
// - When in doubt, KEEP the event. Real bugs hide behind aggressive filters.
// - Document every drop rule with WHY, so future maintainers don't strip
//   useful filters thinking they're stale.
// - Pair this with PostHog's dashboard-side "Suppression rules" for things
//   that are noise today but might become signal later — those can be
//   toggled without a deploy.
// ---------------------------------------------------------------------------
function filterPostHogEvent(event: any): any {
  if (!event) return event;

  // ---------------------------------------------------------------------
  // 0) Bot / headless-browser traffic — applies to ALL events (not just
  //    exceptions), because bot pageviews and pseudo-events pollute
  //    Web Analytics tiles (Visitors, Sessions, Bounce rate) just as
  //    much as bot-caused SDK errors do.
  //
  //    PostHog's project-level "Discard events from known bot user
  //    agents" setting catches Googlebot, Bingbot, etc. — but doesn't
  //    yet include newer AI-agent browsers like Lightpanda. This filter
  //    is defense-in-depth for headless environments that slip through
  //    the built-in list.
  //
  //    Symptom that triggered adding Lightpanda: 582 Segment SDK
  //    "invalid argument" errors on /apartments/... SEO pages, all
  //    from Lightpanda/1.0 UA on a single Canadian IP.
  // ---------------------------------------------------------------------
  const rawUa: string = event.properties?.$raw_user_agent ?? '';
  if (
    /Lightpanda|HeadlessChrome|PhantomJS|Selenium|WebDriver|Puppeteer|Playwright|Cypress|Scrapy|Node-fetch|python-requests|Go-http-client/i.test(
      rawUa,
    )
  ) {
    return null;
  }

  if (event.event !== '$exception') return event;

  const list: any[] = event.properties?.$exception_list ?? [];
  const first = Array.isArray(list) ? list[0] : null;
  if (!first) return event;

  const type: string = first.type ?? '';
  const value: string = first.value ?? '';
  const frames: any[] = first.stacktrace?.frames ?? [];
  const topFrameFile: string = frames[0]?.filename ?? '';

  // 1) Self-healed errors — GlobalRuntimeGuard reloads the user into the new
  //    build when a chunk fails to load (post-deploy chunk mismatch).
  //    Reporting these is pure noise.
  const blob = `${type} ${value}`;
  if (
    blob.includes('ChunkLoadError') ||
    blob.includes('Loading chunk') ||
    blob.includes('Failed to fetch dynamically imported module') ||
    blob.includes('Load failed') ||
    // Webpack bootstrap stale-chunk pattern (same root cause as
    // ChunkLoadError, different surface). GlobalRuntimeGuard catches
    // most of these via the same exact-phrase match and auto-reloads;
    // anything that slips through (e.g. caught by React's error
    // boundary before reaching window.onerror, surfaced as `handled:
    // true`) gets filtered here.
    blob.includes("Cannot read properties of undefined (reading 'call')")
  ) {
    return null;
  }

  // 2) Browser / SDK quirks that are well-known non-bugs.
  if (
    // Benign ResizeObserver warning that fires when an observer callback
    // takes longer than one animation frame. Not actionable.
    /ResizeObserver loop/i.test(value) ||
    // Cross-origin opaque error — browser strips details for security and
    // we have no stack to debug with.
    value === 'Script error.' ||
    // posthog-js wraps non-Error rejections; if we can't get a real type
    // and value, there's nothing actionable here.
    value === 'Non-Error promise rejection captured' ||
    // User navigated away mid-fetch. Happens constantly with Next.js
    // route changes that abort in-flight requests.
    type === 'AbortError' ||
    // User voluntarily cancelled an auth flow (closed the auth sheet or
    // dismissed an OAuth popup). Not a bug — it's a normal user choice.
    // Thrown by `auth-ui-slice.ts` / `useAuthModal.ts` as
    // `AuthCancelledError` so the filter is robust to message changes.
    type === 'AuthCancelledError' ||
    // Network failure on the user's side, surfaced by `@repo/api`'s
    // http-client wrapping browser `TypeError: Failed to fetch`. Almost
    // always means the user lost connectivity mid-request (mobile dead
    // zone, Wi-Fi drop, captive portal, CORS preflight failure on a flaky
    // network). The HTTP client correctly throws so callers can handle it,
    // but reporting every occurrence as a "bug" is noise — there's
    // nothing we can do from code about a user's broken network.
    type === 'NetworkError' ||
    // Defensive: also catch raw `TypeError: Failed to fetch` in case a
    // fetch escapes the http-client wrapper somewhere (e.g. direct calls
    // to `fetch()` from a feature module). Same root cause, same
    // unactionable from our side.
    (type === 'TypeError' && value === 'Failed to fetch') ||
    // Request was aborted by the caller — component unmount mid-fetch,
    // user navigated away, or manual AbortController.abort(). Distinct
    // from real backend timeouts (TimeoutError), which we deliberately
    // keep UNFILTERED because they indicate genuine API slowness worth
    // investigating. See `handleFetchError` in @repo/api http-client.
    type === 'RequestAbortedError' ||
    // Microsoft Office/Outlook/Teams desktop clients inject an RPC layer
    // when users open links from those apps. When the RPC layer fires a
    // postMessage to our page and we don't have the matching handler
    // (because we're not Office), it logs the exact phrase below as an
    // unhandled rejection. Coming from a third-party JS shim Microsoft
    // injects into our page, not from any of our code — and not fixable
    // by us. Well-known noise pattern.
    /Object Not Found Matching Id:\d+, MethodName:/i.test(value) ||
    // Firebase Auth's "INTERNAL ASSERTION FAILED: Pending promise was
    // never set" — a long-standing internal bug in @firebase/auth's
    // Promise polling logic. The error fires during auth flows but the
    // actual auth operation typically still completes successfully. Not
    // fixable from our code; upstream Firebase issue tracked at
    // firebase/firebase-js-sdk on GitHub.
    /INTERNAL ASSERTION FAILED: Pending promise was never set/i.test(value) ||
    // AuthorizationError (HTTP 403) from `@repo/api`'s http-client. The
    // response interceptor in lib/api.ts already handles real session
    // issues (401/403 → session refresh → retry → logout). Any 403 that
    // survives that flow is a genuine "user doesn't have permission for
    // this resource" case — typically a logged-in non-subscribed user
    // hitting subscription-gated endpoints. Not a bug, expected behavior.
    //
    // If we ever see suspiciously high volume here despite this filter,
    // the request URL is attached to the error as `.requestUrl` so we
    // can identify which endpoints are 403'ing and either gate them in
    // the UI or catch gracefully at the call site. AuthenticationError
    // (401) is deliberately NOT filtered — it usually indicates a
    // genuine auth-state bug worth investigating.
    type === 'AuthorizationError' ||
    // NotFoundError (HTTP 404) from `@repo/api`'s http-client. Common
    // sources: autocomplete suggesting a place/building that doesn't
    // have a matching record in our DB, user navigating to a deleted
    // listing, stale recent-search history pointing to removed
    // buildings. These are expected app behavior, not bugs — callers
    // should catch them and either show a "not found" UX or silently
    // fall back. The `requestUrl` is attached to the error so if a
    // specific endpoint starts 404'ing at high volume despite this
    // filter, we can identify it via dashboard breakdowns. Real "this
    // should exist" bugs typically manifest as 500s, not 404s.
    type === 'NotFoundError'
  ) {
    return null;
  }

  // 3) Errors whose top stack frame is outside our own code. Browser
  //    extensions, third-party scripts (GTM, Stripe iframe, Plaid Link,
  //    Facebook pixel, etc.). Our bundles live under `/_next/static/` so
  //    anything else from a different origin is not ours to fix.
  const isExtensionOrigin =
    topFrameFile.startsWith('chrome-extension://') ||
    topFrameFile.startsWith('moz-extension://') ||
    topFrameFile.startsWith('safari-extension://') ||
    topFrameFile.startsWith('safari-web-extension://');

  const KNOWN_THIRD_PARTY_HOSTS =
    /googletagmanager\.com|google-analytics\.com|maps\.googleapis\.com|maps\.gstatic\.com|js\.stripe\.com|cdn\.plaid\.com|connect\.facebook\.net|static\.hotjar\.com|static\.ads-twitter\.com|snap\.licdn\.com|datadoghq-browser-agent\.com|browser\.sentry-cdn\.com/i;

  const isKnownThirdParty = KNOWN_THIRD_PARTY_HOSTS.test(topFrameFile);

  // Errors whose top frame is inside a vendored analytics-SDK bundle that
  // ships embedded in our JS (so they don't have a CDN host in the path).
  // `rrweb-record` is PostHog's session-recording engine — its known bugs
  // (cross-origin iframe handling, etc.) aren't actionable from our code.
  // `maps-api-v3` is Google Maps' internal module path — stack traces
  // sometimes appear with just the path and no host, so we match it
  // separately from the hostname regex above.
  const isVendoredAnalyticsBundle =
    /rrweb[-/]/i.test(topFrameFile) || /\/maps-api-v3\//i.test(topFrameFile);

  if (isExtensionOrigin || isKnownThirdParty || isVendoredAnalyticsBundle) {
    return null;
  }

  // 5) Errors that PostHog couldn't extract a stack trace from. Two
  //    common patterns surface under the literal text PostHog uses:
  //
  //    a) "<EventType> captured as exception with keys: <ownProps>"
  //       — a bare DOM Event (CustomEvent, Event, etc.) was thrown or
  //       used as a Promise rejection. Common sources: WebSocket
  //       onerror, EventSource onerror, image/script onerror handlers,
  //       Safari fetch quirks. Not a real Error, no stack ever existed.
  //
  //    b) "'<TypeName>' captured as exception with message: '<msg>'"
  //       — a real Error came through but lost its stack at some
  //       boundary (Web Worker, postMessage, caught + rethrown as
  //       `new Error(original.message)`). The error is real but we
  //       can't trace it from PostHog. Once a real stack appears (via
  //       source maps + future occurrences), this filter won't catch
  //       it because the boilerplate text won't be there.
  //
  //    Both are unactionable from the dashboard in their current shape
  //    — you'd debug them by instrumenting the suspect code paths
  //    directly, not by reading PostHog issues.
  if (
    type === 'CustomEvent' ||
    type === 'Event' ||
    /captured as exception with (?:keys|message):/i.test(value)
  ) {
    return null;
  }

  // 6) React reconciliation crashes caused by external DOM mutations.
  //    Google Translate (Chrome's built-in translate feature), password
  //    managers, and accessibility extensions all rewrite DOM text nodes
  //    or attributes without React's knowledge. When React next tries to
  //    `removeChild` / `insertBefore` against those nodes, the browser
  //    throws `DOMException: NotFoundError: ... not a child of this
  //    node`. Not actionable from our code — the only "fix" is to disable
  //    translation site-wide, which hurts international users more than
  //    the error matters. See facebook/react#11538.
  //
  //    We target this narrowly: DOMException + the specific error phrase
  //    + react-dom in the top stack frame. A real bug in our own code
  //    doing `removeChild` wouldn't have react-dom in the top frame.
  if (
    type === 'DOMException' &&
    /not a child of this node/i.test(value) &&
    /react-dom/i.test(topFrameFile)
  ) {
    return null;
  }

  // 4) Script-load failures for third-party scripts loaded *by* our own
  //    code or by an analytics SDK (Segment's destination runtime, GTM
  //    loader, FB pixel snippet, etc.). When an adblocker blocks the
  //    underlying script, the JS bundle's `script.onerror` handler fires
  //    and surfaces a "Failed to load <url>" error with the third-party
  //    URL in the message but the SDK's `load-script` helper in the top
  //    frame — so the rule above doesn't catch it. This rule does, by
  //    matching the URL in the error value/message instead of the stack.
  if (/^Failed to load /i.test(value) && KNOWN_THIRD_PARTY_HOSTS.test(value)) {
    return null;
  }

  return event;
}

let analyticsPromise: Promise<Analytics | null> | null = null;
let analyticsInstance: Analytics | null = null;
let scheduledAfterLoad = false;

type AnalyticsWork = (a: Analytics) => unknown | Promise<unknown>;
let pendingWork: AnalyticsWork[] = [];

function flushPending(a: Analytics) {
  const queue = pendingWork;
  pendingWork = [];
  for (const work of queue) {
    try {
      void work(a);
    } catch {
      // best-effort
    }
  }
}

async function initMixpanelViaProxy(params: {
  token: string;
  apiHost: string;
  debug: boolean;
}): Promise<any> {
  // Prefer the official JS SDK (no snippet/eval); send transport through our proxy via api_host.
  const mod = await import('mixpanel-browser');
  const mixpanel = (mod as any).default ?? mod;

  // Mixpanel SDK batches requests and flushes on an interval (often perceived as "slow" in DevTools).
  // We hard-code low-latency settings to make /api/mp/track visible quickly after a user action.
  const batchRequests = true;
  const batchFlushIntervalMs = 500; // default is several seconds; keep low to reduce perceived lag
  const batchSize = 50;

  mixpanel.init(params.token, {
    api_host: params.apiHost,
    // Avoid Next.js trailing-slash normalization (308) by removing Mixpanel's default "/track/".
    // Mixpanel JS supports overriding routes via `api_routes`.
    api_routes: {
      track: 'track',
      engage: 'engage',
      groups: 'groups',
      decide: 'decide',
    },
    // Reduce perceived latency for first events (at the cost of more frequent requests).
    batch_requests: batchRequests,
    batch_flush_interval_ms: batchFlushIntervalMs,
    batch_size: batchSize,
    debug: params.debug,
  });
  return mixpanel;
}

/**
 * Schedule analytics to load after `window.load`.
 *
 * Note: analytics SDKs are still kept out of the main bundle via dynamic imports,
 * but we start initialization ASAP after `load` to minimize user-perceived latency
 * (e.g. first click -> Mixpanel proxy request).
 */
export function scheduleAnalyticsLoadAfterWindowLoad() {
  if (scheduledAfterLoad) return;
  if (typeof window === 'undefined') return;
  scheduledAfterLoad = true;

  const start = () => {
    // Minimize user-perceived delay: kick off immediately after `load`.
    // (We still keep heavy SDKs out of the main bundle via dynamic imports.)
    setTimeout(() => {
      void loadAnalytics()
        .then(a => {
          if (a) flushPending(a);
        })
        .catch(err => {
          // best-effort; queued work remains in memory
          if (process.env.NODE_ENV === 'development') {
            console.error('❌ Analytics load failed', err);
            (window as any).__oi_analytics_load_error = String(err?.message ?? err);
          }
        });
    }, 0);
  };

  // If the `load` event already fired, start immediately.
  if (document.readyState === 'complete') {
    start();
    return;
  }

  window.addEventListener('load', start, { once: true });
}

/**
 * Load and initialize analytics on the client, only once.
 * This defers importing heavy SDKs (e.g., Segment) from the main bundle.
 */
export function loadAnalytics() {
  if (analyticsPromise) return analyticsPromise;

  analyticsPromise = (async (): Promise<Analytics | null> => {
    if (typeof window === 'undefined') return null;

    const writeKey = process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY;
    const mixpanelToken = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;
    const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
    // NEXT_PUBLIC_POSTHOG_HOST is still read by @posthog/nextjs-config during
    // the build for source-map upload. The runtime SDK uses the `/ingest`
    // reverse proxy instead — see posthog.init() below + rewrites in
    // next.config.ts.
    if (process.env.NODE_ENV === 'development') {
      (window as any).__oi_analytics_load_started = true;
      (window as any).__oi_segment_key_present = Boolean(writeKey);
      (window as any).__oi_mixpanel_token_present = Boolean(mixpanelToken);
      (window as any).__oi_posthog_key_present = Boolean(posthogKey);

      // console.info('[analytics] env', {
      //   segment: Boolean(writeKey),
      //   mixpanel: Boolean(mixpanelToken),
      //   posthog: Boolean(posthogKey),
      // });
    }
    if (!writeKey && !mixpanelToken && !posthogKey) return null;

    // Dynamically import deps only in the browser
    const [{ Analytics: AnalyticsCtor }, segmentMod] = await Promise.all([
      import('@repo/analytics'),
      writeKey ? import('@segment/analytics-next') : Promise.resolve(null as any),
    ]);

    const providers: Array<{ name: string; config: Record<string, any> }> = [];

    if (writeKey) {
      const { AnalyticsBrowser } = segmentMod;
      const segmentClient = AnalyticsBrowser.load({ writeKey });
      providers.push({
        name: 'segment',
        config: {
          client: segmentClient,
          debug: process.env.NODE_ENV === 'development',
        },
      });
    }

    if (mixpanelToken) {
      // NOTE: Some blockers match "mixpanel" even on first-party paths.
      // Default to a more neutral path; we support both routes.
      const proxyPath = process.env.NEXT_PUBLIC_MIXPANEL_PROXY_PATH || '/api/mp';
      if (process.env.NODE_ENV === 'development') {
        (window as any).__oi_mixpanel_proxy_path = proxyPath;

        // console.info('[analytics] mixpanel proxy', {
        //   proxyPath,
        //   trackUrl: `${window.location.origin}${proxyPath}/track`,
        // });
      }
      const apiHost = `${window.location.origin}${proxyPath}`;
      let mixpanel: any;
      try {
        mixpanel = await initMixpanelViaProxy({
          token: mixpanelToken,
          apiHost,
          debug: process.env.NODE_ENV === 'development',
        });
        if (process.env.NODE_ENV === 'development') {
          (window as any).__oi_mixpanel_loaded = true;
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ Mixpanel init failed', err);
          (window as any).__oi_mixpanel_error = String((err as any)?.message ?? err);
        }
        throw err;
      }
      providers.push({
        name: 'mixpanel',
        config: {
          client: mixpanel,
          debug: process.env.NODE_ENV === 'development',
        },
      });
    }

    if (posthogKey) {
      try {
        // Dynamically import so posthog-js stays out of the main bundle.
        const phMod = await import('posthog-js');
        const posthog: any = (phMod as any).default ?? phMod;

        // Bootstrap PostHog's distinct id from our existing __anon_id so that
        // pre-login events in PostHog use the same identifier as the rest of
        // our analytics stack (Mixpanel, Segment, the session-id header
        // interceptor). When the user later logs in, AnalyticsEffects calls
        // analytics.setUser(...) → posthog.identify(userId) and PostHog
        // automatically stitches the bootstrapped id to the new one.
        //
        // We call `getAnonymousId()` (not raw localStorage) so the id is
        // CREATED here if it doesn't exist yet — important for first-time
        // visitors where PostHog init might run before any other consumer
        // has triggered id creation. `getAnonymousId()` already handles
        // Safari-private-mode / blocked-storage safely (returns null).
        const bootstrapDistinctId = getAnonymousId() ?? undefined;

        posthog.init(posthogKey, {
          // Route events through our own /ingest reverse proxy so adblockers
          // can't drop PostHog requests. CloudFront serves it on staging and
          // www (infra/web-v4/cdn.tf); under `next dev`, the rewrites in
          // next.config.ts do.
          // `ui_host` keeps SDK-generated links pointing at the real PostHog UI.
          api_host: '/ingest',
          ui_host: 'https://us.posthog.com',
          // We let our typed event registry drive everything custom, so
          // autocapture and the SDK's own pageview event stay off.
          // `capture_pageleave` MUST be on though — PostHog Web Analytics
          // needs $pageleave events to compute bounce rate, session
          // duration, and the new-vs-returning visitor tile.
          autocapture: false,
          capture_pageview: false,
          capture_pageleave: true,
          // Use existing __anon_id as the distinct id when available.
          ...(bootstrapDistinctId ? { bootstrap: { distinctID: bootstrapDistinctId } } : {}),
          // Error tracking: globally capture uncaught exceptions and
          // unhandled promise rejections. PostHog installs its own
          // window.error / unhandledrejection listeners; they coexist with
          // our GlobalRuntimeGuard handlers (both can listen on the same
          // events) without interfering.
          capture_exceptions: true,
          before_send: filterPostHogEvent,
          loaded: () => {
            if (process.env.NODE_ENV === 'development') {
              (window as any).__oi_posthog_loaded = true;
            }
          },
          // PostHog `debug: true` logs every captured event to the
          // console ([PostHog.js] send "$$heatmap" etc.). Useful when
          // building out new event types, noisy otherwise. Off by
          // default in dev — toggle to true locally when you need to
          // inspect outgoing PostHog payloads.
          debug: false,
        });

        providers.push({
          name: 'posthog',
          config: {
            client: posthog,
            debug: process.env.NODE_ENV === 'development',
          },
        });
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ PostHog init failed', err);
          (window as any).__oi_posthog_error = String((err as any)?.message ?? err);
        }
        // Best-effort: continue without PostHog if init fails.
      }
    }

    const analytics = new AnalyticsCtor({
      providers: [
        ...providers,
        {
          name: 'console',
          config: {
            enabled: process.env.NODE_ENV === 'development',
            verbose: true,
            prefix: '🌐',
          },
        },
      ],
      debug: process.env.NODE_ENV === 'development',
      enableAutoContext: true,
    });

    // Default annotation (legacy-style): included in all event payloads
    analytics.annotate({ platform: 'web-v4' });

    await analytics.initialize();
    analyticsInstance = analytics;
    flushPending(analytics);
    return analytics;
  })();

  return analyticsPromise;
}

// Queue work; DOES NOT trigger analytics load.
export async function withAnalytics<T>(fn: (a: Analytics) => T | Promise<T>) {
  if (analyticsInstance) return fn(analyticsInstance);
  if (typeof window === 'undefined') return undefined as unknown as T;
  pendingWork.push(fn as AnalyticsWork);

  // Ensure analytics actually gets a chance to initialize.
  if (!scheduledAfterLoad) {
    scheduleAnalyticsLoadAfterWindowLoad();
  }

  // Minimize click-to-network latency: if a real event was queued, start loading now.
  // This is still dynamic-imported and runs once; it prevents "wait until after load" delays.
  void loadAnalytics()
    .then(a => {
      if (a) flushPending(a);
    })
    .catch(() => {
      // best-effort; errors are surfaced via __oi_analytics_load_error in dev
    });

  if (process.env.NODE_ENV === 'development') {
    (window as any).__oi_pending_analytics_work = pendingWork.length;
  }
  return undefined as unknown as T;
}

export default { loadAnalytics, withAnalytics, scheduleAnalyticsLoadAfterWindowLoad };
